/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import Gutter from '@/components/layout/Gutter';
import { useReplayWorkersMutation } from '@/generated/graphql';
import useDepartment from '@/hooks/useDepartment';
import {
  Col,
  Divider,
  Input,
  Modal,
  Row,
  Tag,
  Typography,
  message
} from 'antd';
import React, { useMemo, useState } from 'react';

interface ReplayRunPopupProps {
  workerId: string;
  visible: boolean;
  onClose: () => void;
}

const ReplayRunPopup = (props: ReplayRunPopupProps) => {
  const { onClose, visible, workerId } = props;
  const { department } = useDepartment();

  const [brainVersion, setBrainVersion] = useState<string>('');

  const [replayWorker, { loading, error }] = useReplayWorkersMutation();

  const handleSubmit = () => {
    replayWorker({
      variables: {
        workerIds: [workerId],
        departmentId: department.id,
        isReplay: true,
        brainVersion
      },
      onCompleted: () => {
        message.success('Replay run submitted successfully');
        onClose();
      },
      onError: (error) => {
        message.error(error.message);
      }
    });
  };

  const isSubmitDisabled = useMemo(() => {
    return loading || !brainVersion;
  }, [brainVersion, loading]);

  return (
    <Modal
      data-cy="replay-run-popup"
      title={
        <Typography.Title level={5}>
          Replay run for Worker ID: <Tag>{workerId}</Tag>
        </Typography.Title>
      }
      open={visible}
      onOk={handleSubmit}
      centered
      okText="Submit"
      okType="primary"
      okButtonProps={{
        'data-cy': 'dry-run-submit',
        disabled: isSubmitDisabled,
        loading
      }}
      onCancel={onClose}
      width={600}
    >
      <Divider />
      <Typography.Text>
        Current brain version:{' '}
        <Tag data-cy="brain-version">{department.brainVersion}</Tag>
      </Typography.Text>
      <Gutter direction="vertical" size={0.5} />
      <Typography.Text>
        {`Verify the worker's behavior by running it with the provided brain version`}
      </Typography.Text>
      <Gutter direction="vertical" size={0.5} />
      <Typography.Text type="secondary">
        NOTE: It will not perform any write operations.
      </Typography.Text>
      <Gutter direction="vertical" size={2} />

      <Row gutter={[12, 12]} align="middle">
        <Col span={5}>
          <Typography.Text strong>Brain version:</Typography.Text>
        </Col>
        <Col span={19}>
          <Input
            value={brainVersion}
            data-cy="brain-version-input"
            onChange={(e) => setBrainVersion(e.target.value)}
          />
        </Col>
      </Row>
      {error && (
        <Typography.Text type="danger">{error.message}</Typography.Text>
      )}
    </Modal>
  );
};

export default ReplayRunPopup;
